import React, { useState, useEffect } from 'react';
import {
    Container,
    Box, Grid,
    Typography,
    useMediaQuery,
    CircularProgress,
} from '@mui/material';

import { Link } from 'react-router-dom';
import { getallBlogs } from '../../services/apiService';
import banner from '../../assets/banner.webp';
import banner2 from '../../assets/banner2.webp';
import banner3 from '../../assets/banner3.webp';

// Fallback images for blogs without images
const fallbackImages = [banner, banner2, banner3];

const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();
const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const Blog = () => {
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoading(true);
                
                // Call the API service which includes fallback mock data
                const response = await getallBlogs();
                
                // Check if response has blogs array - updated for new API structure
                if (response && Array.isArray(response)) {
                    setBlogs(response);
                } else if (response && response.blogs && Array.isArray(response.blogs)) {
                    setBlogs(response.blogs);
                } else if (response && response.blog && Array.isArray(response.blog)) {
                    setBlogs(response.blog);
                } else if (response && response.data && Array.isArray(response.data)) {
                    setBlogs(response.data);
                } else if (response && response.data && response.data.blogs && Array.isArray(response.data.blogs)) {
                    setBlogs(response.data.blogs);
                } else {
                    setBlogs([]);
                }
                
            } catch (error) {
                console.error('Error fetching blogs:', error);
                setError('Failed to load blogs');
                setBlogs([]);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, []);

    // Get fallback image based on blog index
    const getFallbackImage = (index) => {
        return fallbackImages[index % fallbackImages.length];
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        {error}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Please try again later.
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (blogs.length === 0) {
        return (
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No blogs available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Check back later for new content.
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    PropGuide - Real Estate Simplified
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {blogs.map((blog, index) => (
                    <Grid key={blog.id || index} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Link to="/blog-detail" state={{ blog }} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box
                                sx={{
                                    bgcolor: '#fff', borderRadius: 2, overflow: 'hidden',
                                    height: '100%', display: 'flex', flexDirection: 'column',
                                    transition: 'transform 0.3s', cursor: 'pointer',
                                    '&:hover': { transform: 'scale(1.02)' }
                                }}
                            >
                                <Box
                                    component="img"
                                    loading="lazy"
                                    src={blog.blogImage }
                                    alt={blog.title}
                                    sx={{
                                        width: '100%',
                                        height: 190,
                                        objectFit: 'cover',
                                        borderRadius: '8px 8px 0 0',
                                    }}
                                    onError={(e) => {
                                        console.error('Image failed to load:', blog.imageUrl || blog.image);
                                        e.target.src = getFallbackImage(index);
                                    }}
                                />
                                <Box sx={{ p: 2, flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <i className="fas fa-calendar-alt" style={{ color: '#cb4422', fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 13, color: '#555' }}>
                                                {formatDate(blog.createdAt || blog.created_at || new Date())}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <i className="fas fa-clock" style={{ color: '#cb4422', fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 13, color: '#555' }}>
                                                {formatTime(blog.createdAt || blog.created_at || new Date())}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: 16 }}>
                                        {blog.title}
                                    </Typography>
                                    {blog.excerpt && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: 14 }}>
                                            {blog.excerpt.length > 100 ? `${blog.excerpt.substring(0, 100)}...` : blog.excerpt}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Link>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

export default Blog;
